import React, { useRef } from 'react';
import { Control, Controller, UseFormSetValue, useWatch } from 'react-hook-form';
import { Platform, TextInput, View } from 'react-native';

import {
  ControlledSwitchRow,
  FieldBlock,
  OtherChip,
} from '@/components/services/wizard/field';
import { ThemedText } from '@/components/themed-text';
import { PressableScale } from '@/components/ui/pressable-scale';
import { FontPoppins } from '@/constants/font';
import type { ServiceFormData } from '@/lib/validators/service';
import { cn } from '@/utils/cn';
import { formatDuration } from '@/utils/format-appointment';
import { useThemedHex } from '@/hooks/use-theme-colors';

const PRESETS = [15, 30, 45, 60, 90, 120];

interface Props {
  control: Control<ServiceFormData>;
  setValue: UseFormSetValue<ServiceFormData>;
  /** Só o avulso pode não ser agendável — assinatura sem duração não vira horário. */
  allowNotSchedulable: boolean;
}

/**
 * Duração como número editável, no mesmo formato do preço: o valor grande é a
 * resposta, os atalhos são só atalhos.
 *
 * A lista de pills com um "Outro" que abria um campo embaixo obrigava a
 * escolher entre uma opção e o gesto de escapar dela — quem atende em 25 ou em
 * 240 minutos digita direto, e a pill certa acende sozinha quando bate.
 */
export function DurationField({ control, setValue, allowNotSchedulable }: Props) {
  // O "entra na agenda" é campo do formulário, não estado de tela: é ele que
  // torna a duração obrigatória na hora de passar de etapa.
  const schedulable = useWatch({ control, name: 'isSchedulable' }) !== false;

  return (
    <Controller
      control={control}
      name="durationMinutes"
      render={({ field, fieldState }) => (
        <View className="gap-1">
          {allowNotSchedulable ? (
            <ControlledSwitchRow
              control={control}
              name="isSchedulable"
              title="Marca horário na agenda"
              note="Desligado, o serviço só existe em contrato — o cliente não escolhe horário."
              onChange={(next) => {
                if (!next) setValue('durationMinutes', null, { shouldDirty: true });
              }}
              testID="service-wizard-schedulable"
            />
          ) : null}

          {schedulable ? (
            <DurationInput
              value={field.value ?? null}
              onChange={field.onChange}
              error={fieldState.error?.message}
            />
          ) : null}
        </View>
      )}
    />
  );
}

interface InputProps {
  value: number | null;
  onChange: (value: number | null) => void;
  error?: string;
}

function DurationInput({ value, onChange, error }: InputProps) {
  const th = useThemedHex();
  const inputRef = useRef<TextInput>(null);
  const isPreset = value != null && PRESETS.includes(value);

  return (
        <FieldBlock
          title="Duração do atendimento"
          note={value ? formatDuration(value) : undefined}
          error={error}
        >
          <View className="items-center py-1">
            <View className="flex-row items-baseline gap-1.5">
              <TextInput
                ref={inputRef}
                value={value != null ? String(value) : ''}
                onChangeText={(text) => {
                  const digits = text.replace(/\D/g, '').slice(0, 4);
                  onChange(digits ? parseInt(digits, 10) : null);
                }}
                keyboardType="number-pad"
                placeholder="60"
                placeholderTextColor={th('#D4D4D4')}
                accessibilityLabel="Duração em minutos"
                testID="service-wizard-duration-input"
                style={[
                  {
                    fontFamily: FontPoppins.BOLD,
                    fontSize: 38,
                    lineHeight: 48,
                    color: th('#171717'),
                    minWidth: 72,
                    textAlign: 'center',
                    padding: 0,
                  },
                  // No navegador o foco desenha um anel azul do sistema.
                  Platform.OS === 'web' && ({ outline: 'none' } as never),
                ]}
              />
              <ThemedText className="text-[15px] font-semibold text-neutral-400">
                min
              </ThemedText>
            </View>

            <View className="mt-3 flex-row flex-wrap justify-center gap-2">
              {PRESETS.map((minutes) => {
                const active = value === minutes;
                return (
                  <PressableScale
                    key={minutes}
                    haptic
                    accessibilityRole="button"
                    accessibilityState={{ selected: active }}
                    onPress={() => onChange(minutes)}
                    className={cn(
                      'h-9 items-center justify-center rounded-full px-3.5',
                      active ? 'bg-primary' : 'bg-neutral-100',
                    )}
                    style={{ borderCurve: 'continuous' }}
                  >
                    <ThemedText
                      className={cn(
                        'text-[13px] font-semibold',
                        active ? 'text-primary-foreground' : 'text-neutral-700',
                      )}
                    >
                      {minutes}
                    </ThemedText>
                  </PressableScale>
                );
              })}

              {/* O campo grande aceita qualquer minutagem, mas ninguém adivinha
                  isso olhando: a pill limpa e abre o teclado. */}
              <OtherChip
                active={value != null && !isPreset}
                onPress={() => {
                  if (isPreset) onChange(null);
                  inputRef.current?.focus();
                }}
                testID="service-wizard-duration-other"
              />
            </View>
          </View>
        </FieldBlock>
  );
}
